/**
 * Utility functions for normalizing phone numbers and IDs
 */

/**
 * Normalize phone number to international format
 * @param phone - Phone number in any format
 * @returns Normalized phone number (e.g., 263771234567)
 */
export const normalizePhone = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  let digits = phone.replace(/\D/g, '');
  
  // Handle different formats
  if (digits.startsWith('00')) {
    // International format starting with 00
    digits = digits.substring(2);
  } else if (digits.startsWith('0')) {
    // Local format starting with 0
    digits = '263' + digits.substring(1);
  } else if (digits.startsWith('+')) {
    // International format starting with +
    digits = digits.substring(1);
  }
  
  // Ensure single 263 prefix
  if (digits.startsWith('263')) {
    return '263' + digits.substring(3);
  }
  
  return digits;
};

/**
 * Normalize ID (National ID or Company Registration)
 * @param id - ID in any format
 * @returns Normalized ID (uppercase, no spaces, no hyphens)
 */
export const normalizeId = (id: string): string => {
  if (!id) return '';
  
  return id
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .replace(/-/g, '')
    .trim();
};

/**
 * Check if two phone numbers are equal after normalization
 * @param phone1 - First phone number
 * @param phone2 - Second phone number
 * @returns true if normalized numbers are equal
 */
export const phonesEqual = (phone1: string, phone2: string): boolean => {
  return normalizePhone(phone1) === normalizePhone(phone2);
};

/**
 * Check if two IDs are equal after normalization
 * @param id1 - First ID
 * @param id2 - Second ID
 * @returns true if normalized IDs are equal
 */
export const idsEqual = (id1: string, id2: string): boolean => {
  return normalizeId(id1) === normalizeId(id2);
};

/**
 * Validate Zimbabwe National ID format.
 * Accepts common forms such as:
 * - 12-345678Z12
 * - 12-3456789Z12
 * - 63-1192059 Z 50
 * - 12345678Z12
 */
export const isValidZimbabweNationalId = (id: string): boolean => {
  if (!id?.trim()) return false;
  const cleaned = id.toUpperCase().replace(/[\s-]/g, '');
  return /^\d{8,9}[A-Z]\d{2}$/.test(cleaned);
};

/**
 * Format phone number for display
 * @param phone - Phone number
 * @returns Formatted phone number (e.g., +263 77 123 4567)
 */
export const formatPhoneForDisplay = (phone: string): string => {
  const normalized = normalizePhone(phone);
  if (normalized.length < 9) return phone;
  
  const countryCode = normalized.substring(0, 3);
  const number = normalized.substring(3);
  
  if (number.length >= 9) {
    return `+${countryCode} ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
  }
  
  return `+${normalized}`;
};

/**
 * Get last 4 digits of phone number for masking
 * @param phone - Phone number
 * @returns Last 4 digits or original if too short
 */
export const getLast4Digits = (phone: string): string => {
  const normalized = normalizePhone(phone);
  return normalized.length >= 4 ? normalized.substring(normalized.length - 4) : normalized;
};
